import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { tap, pluck, catchError } from 'rxjs/operators';
import { FeatureGroup, LatLngBounds } from 'leaflet';
import * as Highcharts from 'highcharts/highcharts-gantt';
// Services
import { SharedRelayService } from 'src/app/services/shared-relay.service';
import { DrawTrajectoryService } from 'src/app/services/draw-trajectory.service';
import { CoreService } from './core.service';
// Models
import { AutopilotTrajectory, MultipleApiResponse } from '@mocca-technologies/model';
import { SCHEDULED_OPERATIONS } from '../utils/routing/routes.model';
import ScheduleActions from '../pages/scheduled-operations-page/state/actions';
// Enums
import { ScheduledOperationStatusColorEnum, ScheduledOperationStatusEnum } from '@mocca-technologies/model';

@Injectable()
export class ScheduleService {
    // Options for parameters-modal inputs
    vehicles = [];
    vertiports = [];

    // parameters-modal selected values
    readonly msPerDay = 24 * 60 * 60 * 1000;
    readonly timezoneOffset = new Date().getTimezoneOffset();
    filterParameters = {
        vehicleIdList: null,
        vertiport: null,
        startTime: null,
        endTime: null,
        tobeScheduled: false,
        location: null
    };

    map = null;
    layers: any[];
    overlays = new Map();

    updateChartFlag = false;
    chartOptions!: Highcharts.Options;
    chart: any;

    scrollTo = new Subject<string>();
    cacheSelection = {};

    constructor(
        private http: HttpClient,
        private store: Store,
        private sharedRelayService: SharedRelayService,
        private drawTrajectoryService: DrawTrajectoryService,
        private coreService: CoreService,
    ) {
        const self = this;
        this.chartOptions = {
            title: {
                text: ''
            },

            tooltip: {
                shared: true,
                useHTML: true,
                formatter() {
                    const { name } = this.points[0].point;
                    return name;
                }
            },
            xAxis: {
                minRange: 3600 * 1000,
                events: {
                    setExtremes: function (e) {
                        if (e.trigger === 'zoom') {
                            this.chart.showResetZoom();
                        }
                    }
                },
                labels: {
                    align: 'center',
                    style: {
                        fontWeight: 'bold',
                    }
                },
                plotBands: [{
                    label: {
                        align: 'center',
                        style: {
                            fontWeight: 'bold',
                        }
                    }
                }]
            },
            yAxis: {
                categories: [''],
                visible: false,
            },
            navigator: {
                enabled: true,
            },
            scrollbar: {
                enabled: false,
            },
            series: [
                {
                    animation: false,
                    type: 'gantt',
                    cursor: 'pointer',
                    point: {
                        events: {
                            click() {
                                const { id, start } = this as any;

                                self.zoomToOperation(start);

                                self.store.dispatch(ScheduleActions.setActive({ id: id }));
                                self.scrollTo.next(id);
                            }
                        }
                    },
                    dataLabels: {
                        enabled: true
                    },
                    data: [{}]
                }
            ],
            time: {
                timezoneOffset: this.timezoneOffset
            },

            chart: {
                type: 'gantt',
                events: {
                    load() {
                        self.chart = this;
                    }
                }
            },


        };
    }

    /**
     * This method is used to refresh the list of vertiports
     */
    refreshVertiportList() {
        const list = Array.from(this.sharedRelayService.items);
        this.vertiports = list.filter((item) => item.kind === 'vertiport');
    }

    /**
     * This method is used to paint a trajectory on the map
     * @param uniqueId
     * @param map
     */
    drawTrajectory(uniqueId, map) {
        if(uniqueId){
            const httpParams = new HttpParams().set('id', uniqueId);

            //pluck('autopilotOperationExt', 'mainTrajectory'),
            this.http
                .get(SCHEDULED_OPERATIONS, {params: httpParams, observe: 'response'})
                .pipe(
                    tap((res: HttpResponse<MultipleApiResponse>) => (this.sharedRelayService.schOperation = res?.body?.responseObject as AutopilotTrajectory)),
                    catchError((error) => {
                        throw error;
                    })
                )
                .subscribe({
                    next: (res: HttpResponse<MultipleApiResponse>) => {
                        const mainTrajectory = res?.body?.responseObject as AutopilotTrajectory;
                        this.layers = this.drawTrajectoryService.draw(mainTrajectory, this.overlays);
                        const bounds = new LatLngBounds([]);
                        this.layers.forEach((layer: FeatureGroup) => {
                            layer.addTo(map);
                            bounds.extend(layer.getBounds());
                        });
                        map.fitBounds(bounds);
                    },
                    error: (error: HttpErrorResponse) => {
                        this.coreService.showServerResponse(error?.status, error?.error);
                    }
            });
        }
    }

    /**
     * This method is used to create and update the scheduled operations in the Gantt
     * @param operations
     */
    updateGanttSeries(operations) {
        if(operations.length > 0){
            if(operations[0].scheduledTime.operationStartTime){
                this.updateChartFlag = true;

                (this.chartOptions.series[0] as any).data = [
                    ...operations.map((operation) =>
                            ({
                                id: operation.uniqueId,
                                start: operation.scheduledTime.operationStartTime.value,
                                end: operation.scheduledTime.operationEndTime.value,
                                name: operation.uspaceId ? operation.callsign + ' - ' + operation.uspaceId : operation.callsign,
                                labelrank: 0,
                                color: this.convertIonicolorToHex(this.getStatusColor(operation.uspaceAuthorized)),
                                y: 0
                            }))
                ];
            }
        }
    }

    /**
     * This method is used to paint a selected operation in the Gantt
     * @param operation
     */
    drawSelectionOnGantt(operation) {
        if(operation.scheduledTime.operationStartTime) {
            (this.chartOptions.series[0] as any).data.pop(this.cacheSelection);
            this.updateChartFlag = true;

            this.cacheSelection = {
                id: 'currentSelection',
                start: operation.scheduledTime.operationStartTime.value,
                end: operation.scheduledTime.operationEndTime.value,
                name: operation.uspaceId ? operation.callsign + ' - ' + operation.uspaceId : operation.callsign,
                color: 'green',
                labelrank: 100,
                y: 0
            };

            (this.chartOptions.series[0] as any).data.push(this.cacheSelection);
        }
    }

    /**
     * This method is used to change the aslogic state
     * @param schOpParameters
     * @returns
     */
    updateSchOperationRequest(schOpParameters) {
        const body = { schOpParameters };
        return this.http.put(SCHEDULED_OPERATIONS, body, {
            observe: 'response',
            withCredentials: true
        });
    }

    /**
     * This method is used to select todays date
     * @returns
     */
    initDate() {
        const epochTime = new Date().getTime();
        const epochOffset = this.timezoneOffset * 60 * 1000;
        const startTimestamp = (epochTime - epochOffset) / this.msPerDay;

        return Math.floor(startTimestamp) * this.msPerDay;
    }

    /**
     * This method is used to create a scheduled operation
     * @param body
     * @returns
     */
    createScheduled(body): Observable<HttpResponse<MultipleApiResponse>>  {
        return this.http.post<MultipleApiResponse>(SCHEDULED_OPERATIONS, body, {
            observe: 'response',
            withCredentials: true
        });
    }

    /**
     * This method is used to get the status color of an sch operation depending on the aslogic status
     * @param status
     * @returns
     */
    getStatusColor(status: string): string {
        let statusEnum: ScheduledOperationStatusEnum = status as unknown as ScheduledOperationStatusEnum;

        return ScheduledOperationStatusColorEnum[statusEnum];
    }

    /**
     * This method is used to convert an ionic color type to a hexadecimal color type
     * @param color
     * @returns
     */
    convertIonicolorToHex(color: string): string {
        switch (color) {
            case 'primary':
                return '#007bff';
            case 'secondary':
                return '#6c757d';
            case 'success':
                return '#28a745';
            case 'danger':
                return '#dc3545';
            case 'warning':
                return '#ffc107';
            case 'info':
                return '#17a2b8';
            case 'light':
                return '#f8f9fa';
            case 'dark':
                return '#343a40';
            case 'redsalsa':
                return '#ff3e4d';
            case 'medium':
                return '#b5b5b5';
            case 'yelloworange':
                return '#ffae42';
            case 'dimmed':
                return '#b5b5b5';
            case 'maizecrayola':
                return '#f2c94c';
            case 'pistachio':
                return '#93c572';
            default:
                return '#007bff';
        }
    }

    /**
     * This method is used to make a zoom of 6 hrs before and after the selected hour
     * @param start
     */
    zoomToOperation(start: number) {

        // !environment.production && console.log(start);

        const min = start - 6 * 3600 * 1000;
        const max = start + 6 * 3600 * 1000;

        if (this.chart && this.chart.xAxis && this.chart.xAxis[0]) {

            // !environment.production && console.log("Zooming to:", min, max);
            const xAxis = this.chart.xAxis[0];

            xAxis.setExtremes(min, max);
        }
    }
}
